import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import UserContextMenu from './UserContextMenu';
import NotificationIndicator from './NotificationIndicator';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperAdmin } from '../../utils/roles';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface HeaderProps {
  onMenuToggle?: () => void;
  isMobileMenuOpen?: boolean;
}

const Header = ({ onMenuToggle, isMobileMenuOpen = false }: HeaderProps) => {
  const { userProfile, effectiveRole, user } = useAuth();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [companyName, setCompanyName] = useState<string>('Nasuman');
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);

  const location = useLocation();
  
  // Determine user role for UI/nav:
  // effectiveRole accounts for group_members.role='admin' (scoped admin) in addition to platform roles.
  const userRole = effectiveRole;
  const isSuperAdminUser = isSuperAdmin(userProfile?.role);
  const isGroupAdminUser = userProfile?.role === 'group_admin'; // Only group_admin, not super_admin

  // Helper function to convert logo URL to public URL
  const convertLogoUrl = (logoUrl: string | null): string | null => {
    if (!logoUrl || logoUrl.trim() === '') return null;
    
    // If it's already a full URL (starts with http/https), use it as is
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      return logoUrl;
    }
    // If it's a storage path (contains 'company-logos'), convert to public URL
    else if (logoUrl.includes('company-logos')) {
      // Extract the path after 'company-logos/'
      const pathMatch = logoUrl.match(/company-logos\/(.+)/);
      if (pathMatch) {
        const filePath = pathMatch[1];
        const { data: publicUrlData } = supabase.storage
          .from('company-logos')
          .getPublicUrl(filePath);
        return publicUrlData.publicUrl;
      } else {
        return logoUrl;
      }
    }
    // If it's a relative path starting with /, use it as is
    else if (logoUrl.startsWith('/')) {
      return logoUrl;
    }
    // Otherwise, assume it's a storage path and try to convert
    else {
      const { data: publicUrlData } = supabase.storage
        .from('company-logos')
        .getPublicUrl(logoUrl);
      return publicUrlData.publicUrl;
    }
  };

  // Load company branding from user profile, fallback to group logo
  useEffect(() => {
    const loadCompanyBranding = async () => {
      if (!user?.id) {
        console.log('[Header] No user ID, skipping logo load');
        return;
      }
      try {
        console.log('[Header] Loading company branding for user:', user.id);
        
        // First, try to get user's profile branding
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('company_name, company_logo_url')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('[Header] Error loading user profile branding:', profileError);
        }

        let logoUrl: string | null = null;
        let companyName: string = 'Nasuman';

        // Priority 1: User's profile logo/name
        if (userProfile?.company_logo_url && userProfile.company_logo_url.trim() !== '') {
          logoUrl = userProfile.company_logo_url;
          companyName = userProfile.company_name || 'Nasuman';
          console.log('[Header] Using user profile logo:', logoUrl);
        } 
        // Priority 2: Check user's groups for logo
        else {
          console.log('[Header] No user profile logo, checking groups...');
          
          // Get user's groups
          const { data: groupMemberships, error: groupsError } = await supabase
            .from('group_members')
            .select('group_id, groups!inner(id, company_name, company_logo_url, created_by)')
            .eq('user_id', user.id)
            .limit(10);

          if (!groupsError && groupMemberships && groupMemberships.length > 0) {
            // Find first group with a logo
            for (const membership of groupMemberships) {
              const group = membership.groups as any;
              if (group?.company_logo_url && group.company_logo_url.trim() !== '') {
                logoUrl = group.company_logo_url;
                companyName = group.company_name || group.name || 'Nasuman';
                console.log('[Header] Found group logo:', logoUrl, 'from group:', group.name);
                break;
              }
              // If group doesn't have logo, check creator's logo
              else if (group?.created_by) {
                const { data: creatorProfile } = await supabase
                  .from('user_profiles')
                  .select('company_name, company_logo_url')
                  .eq('id', group.created_by)
                  .single();
                
                if (creatorProfile?.company_logo_url && creatorProfile.company_logo_url.trim() !== '') {
                  logoUrl = creatorProfile.company_logo_url;
                  companyName = creatorProfile.company_name || group.name || 'Nasuman';
                  console.log('[Header] Found creator logo:', logoUrl, 'from group:', group.name);
                  break;
                }
              }
            }
          }
        }

        // Convert logo URL to public URL if needed
        const finalLogoUrl = convertLogoUrl(logoUrl);
        
        setCompanyName(companyName);
        setCompanyLogoUrl(finalLogoUrl);
        
        console.log('[Header] Final branding:', {
          companyName,
          logoUrl: finalLogoUrl
        });
      } catch (e) {
        console.error('[Header] Error loading company branding:', e);
      }
    };

    loadCompanyBranding();
  }, [user?.id]);

  // Update date and time every second for group_admin
  useEffect(() => {
    if (isGroupAdminUser && !isSuperAdminUser) {
      const timer = setInterval(() => {
        setCurrentDateTime(new Date());
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isGroupAdminUser, isSuperAdminUser]);

  // Format date and time
  const formatDateTime = (date: Date) => {
    const dateStr = date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    return { dateStr, timeStr };
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navigationItems: Array<{
    label: string;
    path: string;
    icon: string;
    roles: string[];
    showForSuperAdmin?: boolean;
  }> = [
    {
      label: 'Dashboard',
      path: userRole === 'admin' ? '/admin-dashboard' : '/member-dashboard',
      icon: 'LayoutDashboard',
      roles: ['admin', 'member'],
    },
    {
      label: 'Management',
      path: '/group-management',
      icon: 'Settings',
      roles: ['admin'],
      // Show for both group_admin and super_admin
      showForSuperAdmin: true,
    },
    {
      label: 'Invoices',
      path: '/invoices',
      icon: 'FileText',
      roles: ['admin'],
      showForSuperAdmin: true,
    },
    {
      label: 'Analytics',
      path: '/advanced-analytics',
      icon: 'BarChart3',
      roles: ['admin'],
      showForSuperAdmin: true,
    },
    {
      label: 'Contributions',
      path: '/contribution-recording',
      icon: 'DollarSign',
      roles: ['admin'],
      // Hide for super_admin (they can't record contributions)
      showForSuperAdmin: false,
    },
    {
      label: 'Reports',
      path: '/reports',
      icon: 'FileText',
      roles: ['admin', 'member'],
    },
  ];

  const filteredNavItems = navigationItems.filter(item => {
    // Check if role matches
    if (!item.roles.includes(userRole)) return false;
    
    // If item has showForSuperAdmin property, check it
    if ('showForSuperAdmin' in item && item.showForSuperAdmin === false) {
      // Hide this item for super_admin
      if (isSuperAdminUser) return false;
    }
    
    return true;
  });

  return (
    <header className={`sticky top-0 z-50 w-full border-b border-border backdrop-blur supports-[backdrop-filter]:bg-card/60 ${
      isGroupAdminUser && !isSuperAdminUser 
        ? 'bg-gradient-to-br from-blue-50 via-white to-emerald-50' 
        : 'bg-card/95'
    }`}>
      <div className="h-full flex items-center justify-between px-4 lg:px-6">
        <div className="flex items-center gap-6">
          <Link to={userRole === 'admin' ? '/admin-dashboard' : '/member-dashboard'} className="flex items-center gap-2 transition-interactive hover:opacity-80">
            {companyLogoUrl ? (
              <img
                src={companyLogoUrl}
                alt={companyName}
                className="w-10 h-10 object-contain rounded-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
                <Icon name="Wallet" size={24} color="var(--color-primary-foreground)" />
              </div>
            )}
            <span className="text-xl font-semibold text-foreground hidden sm:block">
              {companyName}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-3">
            {filteredNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-sm
                  transition-interactive hover-scale
                  ${isActivePath(item.path)
                    ? 'bg-primary text-primary-foreground shadow-elevation-1'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={item.icon} size={18} />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          {/* Show date and time for group_admin only (not super_admin) */}
          {isGroupAdminUser && !isSuperAdminUser && (
            <div className="hidden md:flex flex-col items-end text-right">
              <div className="text-xs text-muted-foreground">
                {formatDateTime(currentDateTime).dateStr}
              </div>
              <div className="text-sm font-medium text-foreground flex items-center gap-1">
                <Icon name="Clock" size={14} />
                {formatDateTime(currentDateTime).timeStr}
              </div>
            </div>
          )}
          <NotificationIndicator />
          <UserContextMenu userRole={userRole} />
          
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 rounded-sm hover:bg-muted transition-interactive"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-20 left-0 right-0 bg-card border-b border-border shadow-elevation-2 z-1100">
          <nav className="flex flex-col p-4 gap-2">
            {filteredNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={onMenuToggle}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-sm
                  transition-interactive
                  ${isActivePath(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={item.icon} size={20} />
                <span className="text-base font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>
      )}

    </header>
  );
};

export default Header;